/**
 * SupplierPicker — Issue #426.
 *
 * Fuzzy supplier picker used by the purchase order create dialog. The picker
 * lets operators search current tenant/org suppliers by name or code while
 * the form still submits the stable internal `supplier.id`.
 *
 * Red lines:
 *   - The form value (`value.id`) is ALWAYS the real backend supplier id and
 *     is the only thing ever submitted. Typed text is never echoed back to
 *     `supplierId`.
 *   - Backend `tenantId/orgId/deletedAt/enabled` scoping and `riskLevel` /
 *     blocked / disabled gating stay enforced by the API. The picker only
 *     surfaces the same enabled list and additionally hides `blocked`
 *     suppliers from selection (defence in depth — the backend would still
 *     reject them at create time).
 *   - Only non-sensitive summary fields (id, code, name, category,
 *     riskLevel, enabled) are rendered — contactChannel /
 *     qualificationSummary stay detail-only on the backend.
 *   - Editing the search text after a selection clears the selection, so
 *     operators must re-select before the form will submit again.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useSupplierList,
  type SupplierSummaryDto,
} from '@/hooks/useSupplier';

export interface SupplierPickerProps {
  id?: string;
  value: SupplierSummaryDto | null;
  onChange: (next: SupplierSummaryDto | null) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Suggestion cap. Server clamps the list endpoint to its own max. */
  limit?: number;
  /** Debounce in ms. Defaults to 250. */
  debounceMs?: number;
  /** test id for the wrapper element, mainly for component tests. */
  testId?: string;
}

// Risk labels mirror the supplier domain enum
// (`SUPPLIER_RISK_LEVELS = ['normal', 'watch', 'high', 'blocked']`) and the
// labels already used by `SuppliersPage.tsx`, so the picker never surfaces
// raw English values such as `normal` or `watch` to operators.
const RISK_LABEL: Record<string, string> = {
  normal: '正常',
  watch: '关注',
  high: '高风险',
  blocked: '禁用',
};

const CATEGORY_LABEL: Record<string, string> = {
  consumable: '耗材',
  reagent: '试剂',
  equipment: '设备',
  service: '服务',
  logistics: '物流',
  other: '其它',
};

function formatLabel(s: SupplierSummaryDto): string {
  return `${s.code} | ${s.name}`;
}

export function SupplierPicker(props: SupplierPickerProps): ReactNode {
  const {
    id: idProp,
    value,
    onChange,
    required,
    disabled,
    className,
    placeholder = '按供应商名称或编码搜索…',
    limit = 20,
    debounceMs = 250,
    testId,
  } = props;
  const reactId = useId();
  const inputId = idProp ?? `supplier-picker-${reactId}`;
  const listboxId = `${inputId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, error, fetchList } = useSupplierList();

  const runFetch = useCallback(
    (q: string) => {
      void fetchList({
        enabled: true,
        search: q.trim() || undefined,
        limit,
      });
    },
    [fetchList, limit],
  );

  // Debounced fetch when the picker is open and disabled is false.
  useEffect(() => {
    if (!open || disabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runFetch(query);
    }, debounceMs);
    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, disabled, query, debounceMs, runFetch]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return (): void => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  // Defence in depth: filter out blocked / disabled suppliers from the
  // candidate list. The backend would also reject them at create time, but
  // hiding them avoids tempting the user to pick something that will 400.
  const items = useMemo<readonly SupplierSummaryDto[]>(() => {
    const rows = data?.rows ?? [];
    return rows.filter(
      (s) => s.enabled === true && s.riskLevel !== 'blocked',
    );
  }, [data]);

  useEffect(() => {
    setActiveIndex(0);
  }, [items.length]);

  const displayValue = open ? query : value ? formatLabel(value) : '';

  const handleSelect = (item: SupplierSummaryDto): void => {
    onChange(item);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (): void => {
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  const handleFocus = (): void => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const next = event.target.value;
    setQuery(next);
    if (!open) setOpen(true);
    // Editing the search text after a selection clears the selected supplier
    // so the form cannot accidentally submit a stale id alongside a new typed
    // keyword. This is one of the acceptance criteria in Issue #426.
    if (value !== null) onChange(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((prev) =>
        items.length === 0 ? 0 : (prev + 1) % items.length,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) =>
        items.length === 0 ? 0 : (prev - 1 + items.length) % items.length,
      );
    } else if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      const candidate = items[activeIndex];
      if (candidate) handleSelect(candidate);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const showDropdown = open && !disabled;
  const showEmpty =
    showDropdown && !loading && error === null && items.length === 0;

  return (
    <div
      ref={wrapperRef}
      className={cn('relative w-full', className)}
      data-testid={testId ?? 'supplier-picker'}
    >
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && items[activeIndex]
              ? `${listboxId}-option-${activeIndex}`
              : undefined
          }
          required={required && !value}
          disabled={disabled}
          value={displayValue}
          placeholder={placeholder}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          data-testid="supplier-picker-input"
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent pl-8 pr-9 py-1 text-sm shadow-sm transition-colors',
            'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
        {value && !disabled ? (
          <button
            type="button"
            aria-label="清除已选供应商"
            onClick={handleClear}
            data-testid="supplier-picker-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {value ? (
        <p
          className="mt-1 text-xs text-muted-foreground"
          data-testid="supplier-picker-selected-label"
        >
          已选择：<span className="font-medium">{formatLabel(value)}</span>
        </p>
      ) : null}

      {showDropdown ? (
        <div
          id={listboxId}
          role="listbox"
          data-testid="supplier-picker-listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {loading ? (
            <div
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              加载中…
            </div>
          ) : null}

          {!loading && error ? (
            <div
              className="px-3 py-2 text-sm text-destructive"
              role="alert"
              data-testid="supplier-picker-error"
            >
              {error.message}
            </div>
          ) : null}

          {showEmpty ? (
            <div
              className="px-3 py-2 text-sm text-muted-foreground"
              role="status"
              data-testid="supplier-picker-empty"
            >
              {query.trim().length > 0
                ? '没有匹配的供应商，换个关键字试试'
                : '当前没有可选择的启用供应商，请先到供应商档案启用后再创建采购订单'}
            </div>
          ) : null}

          {!loading && !error && items.length > 0
            ? items.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  id={`${listboxId}-option-${index}`}
                  aria-selected={activeIndex === index}
                  data-testid={`supplier-picker-option-${item.id}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handleSelect(item)}
                  className={cn(
                    'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                    activeIndex === index
                      ? 'bg-accent text-accent-foreground'
                      : null,
                  )}
                >
                  <span className="font-medium">{formatLabel(item)}</span>
                  <span className="text-xs text-muted-foreground">
                    {CATEGORY_LABEL[item.category] ?? item.category} ·{' '}
                    {RISK_LABEL[item.riskLevel] ?? item.riskLevel} ·{' '}
                    {item.enabled ? '启用' : '停用'}
                  </span>
                </button>
              ))
            : null}
        </div>
      ) : null}
    </div>
  );
}
